import { useTranslation } from "react-i18next";
import { FontSize, Text } from "../../../components/Text";
import { Flex } from "../../../components/Flex";
import { useAppSelector } from "../../../redux/store";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Spacing } from "../../../theme/Spacing";
import styled from "styled-components";
import { renderHighlight } from "../utils/searchHighlight";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import isNumber from "lodash/isNumber";

const IconWrapper = styled(Flex)`
  vertical-align: middle;
  margin-inline-end: ${Spacing.xs};
  display: inline-flex;
  justify-content: center;
  width: 1rem;
`;

const EntryDetail = styled.li`
  margin-inline-start: ${Spacing.xl};
  font-size: ${FontSize.m};

  &::marker {
    color: ${({ theme }) => theme.main};
  }
`;

const DetailsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

type Props = {
  instance: BaseModuleInstance & { parentModule?: { title?: string } };
};

export const SearchCardModuleInstance: React.FC<Props> = ({ instance }) => {
  const { t } = useTranslation();

  const parentModuleDefinitionInputs =
    useAppSelector((s) =>
      moduleSliceSelectors.selectModuleDefinitionById(
        s,
        instance.parentId || "",
      ),
    )?.inputs || [];

  return (
    <Flex flexDirection="column" gap="s" flexGrow={1}>
      <Text className="atira-search-text">
        <IconWrapper>
          <AtiraIcon icon={faCube} />
        </IconWrapper>
        <strong>{t("common.module")}: </strong>
        <span>{instance.parentModule?.title}</span>
      </Text>

      <DetailsList>
        {instance.name?.trim().length ? (
          <EntryDetail className="atira-search-text">
            <strong>{t("common.moduleInstance")}: </strong>
            <span>{instance.name}</span>
          </EntryDetail>
        ) : null}

        {isNumber(instance.sequenceId) ? (
          <EntryDetail className="atira-search-text">
            <strong>{t("modules.instance.ids.title")}: </strong>
            <span>{instance.sequenceId}</span>
          </EntryDetail>
        ) : null}

        {Object.entries(instance.fields || {}).map(([key, value]) => {
          const highlights = renderHighlight(instance, key);
          const label = parentModuleDefinitionInputs.find(
            (i) => i.name === key,
          )?.label;

          return (
            <EntryDetail key={key} className="atira-search-text">
              <strong>{label}: </strong>
              <bdi>{highlights ? highlights : String(value)}</bdi>
            </EntryDetail>
          );
        })}
      </DetailsList>
    </Flex>
  );
};
